import FilterDropdownMenu from "components/CRM/FilterDropdownMenu";
import AppDrawer from "components/Drawers/AppDrawer";
import RequiredTitle from "elements/RequiredTitle";
import useReadableText from "hooks/useReadableText";
import { useState, useEffect } from "react";
import { Alert, Button, Input } from "reactstrap";
import { isValidQuery } from "utils/QueryUtils";
import { newToastEvent } from "utils/ToastUtil";
import AppSpinner from "../../elements/AppSpinner";
import { Toggle } from "rsuite";
import useErrorMessage from "../../hooks/useErrorMessage";

const CreateFilterDrawer = ({
  open,
  onClose,
  onExited,
  currentModule,
  handleCreate,
  handleUpdate,
  submitting,
  existingFilter = null, // Used when Updating an existing filter.
  prefillFilter = null, // Used when Creating a new filter, prefills the filters section
  createKey,
  updateKey,
  checkForFields,
  subitemFieldsOnly,
  showVariables = false,
}) => {
  const [query, setQuery] = useState(() => {
    if (!existingFilter?.Filters) return null;

    return { Filters: existingFilter?.Filters };
  });
  const { error, setError, clearError } = useErrorMessage();
  const [name, setName] = useState(existingFilter?.Name ?? "");
  const [filterLimit, setFilterLimit] = useState(
    !!existingFilter?.Limit?.Count
  );
  const [limit, setLimit] = useState(existingFilter?.Limit?.Count);
  const { readableText } = useReadableText({
    query,
    moduleAPIName: currentModule?.APIName || currentModule?.Title,
  });

  useEffect(() => {
    if (!prefillFilter) return;

    setName("");
    setLimit(undefined);
    setQuery({ Filters: prefillFilter?.Filters ?? [] });
  }, [prefillFilter]);

  const isValid = isValidQuery(query, false);

  return (
    <AppDrawer
      open={open}
      onClose={onClose}
      onExited={onExited}
      error={error ? { message: error, onClear: clearError } : undefined}
      size="md"
      headerTitle={`${existingFilter ? "Update" : "Create"} Filter`}
      headerDescription={
        existingFilter
          ? `Updating a saved filter`
          : "Creating a filter that will be saved in this page"
      }
      headerOptions={
        <Button
          size="sm"
          color="primary"
          onClick={handleCreateUpdateFilter}
          disabled={submitting || !isValid || !name}
          style={{ width: 100 }}
        >
          {submitting ? (
            <AppSpinner size="sm" color="white" />
          ) : existingFilter ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      }
      body={
        <>
          <RequiredTitle title="Filter Name" size="md" />
          <Input value={name} onChange={(e) => setName(e.target.value)} />
          <Button outline disabled={!query} onClick={replaceWithReadableText}>
            Use Recommended Name
          </Button>
          <hr />
          <RequiredTitle title="Select Filters" size="md" />
          <div className="border rounded">
            <FilterDropdownMenu
              currentModule={currentModule}
              query={query}
              onApply={(out, close = true, type) => {
                const newQuery = { ...query };

                newQuery.Filters = out?.Filters;

                // The type is "advanced" or "quick"
                // We can use this prop to display quick/advanced filters on mount
                if (!newQuery?.Metadata) {
                  newQuery.Metadata = {
                    Type: type,
                  };
                } else {
                  newQuery.Metadata = {
                    ...newQuery.Metadata,
                    Type: type,
                  };
                }

                setQuery(newQuery);
              }}
              onClear={() => setQuery(null)}
              onClose={null}
              applyOnChange={true}
              startingMode={query?.Metadata?.Type ?? "advanced"}
              checkForFields={checkForFields}
              subitemFieldsOnly={subitemFieldsOnly}
              showVariables={showVariables}
            />
          </div>
          {name && query && !isValid && (
            <Alert
              color="warning"
              className="mt-4 p-2 d-flex align-items-center"
              style={{ fontSize: 16 }}
            >
              <i
                className="mdi mdi-alert-circle-outline mr-2"
                style={{ fontSize: 18 }}
              />
              The selected filters are invalid. Please ensure all fields are
              filled out.
            </Alert>
          )}
          <hr />
          <div className="d-flex flex-column">
            <h4 className="mb-0 pb-0">Limit Records</h4>
            <small className="text-medium pb-2">
              Select the limit of records displayed.
            </small>
            <Toggle
              className="mb-3"
              onChange={() => limitToggle()}
              checked={filterLimit}
            />
            {filterLimit && (
              <div>
                <Input
                  onChange={(e) => {
                    const newLimit = parseInt(e.target.value);

                    if (newLimit < 1) return;
                    if (newLimit > 500) {
                      return setError(
                        "Limit must be a number between 0 and 500"
                      );
                    }

                    setLimit(newLimit);
                  }}
                  type="number"
                  placeholder="Limit"
                  bsSize="sm"
                  style={{ width: 100 }}
                  value={limit ?? ""}
                />
              </div>
            )}
          </div>
        </>
      }
    />
  );

  function reset() {
    setName("");
    setLimit(undefined);
    setQuery(null);
  }

  function onSuccess() {
    const message = existingFilter
      ? "Successfully updated filter"
      : "Successfully created filter";
    reset();
    onClose();
    newToastEvent("success", message);
  }

  function replaceWithReadableText() {
    setName(readableText);
  }

  function handleCreateUpdateFilter() {
    const payload = {
      Name: name,
      Filters: query?.Filters ?? [],
      Type: query?.Metadata?.Type ?? "advanced",
      Limit: {
        Count: limit,
      },
    };

    if (existingFilter) {
      handleUpdate({
        key: updateKey,
        id: existingFilter.id,
        payload: payload,
        onSuccess: onSuccess,
      });
    } else {
      handleCreate({
        key: createKey,
        payload: payload,
        onSuccess: onSuccess,
      });
    }
  }

  function limitToggle() {
    setFilterLimit(!filterLimit);
    setLimit(undefined);
  }
};

export default CreateFilterDrawer;
